import type { ru } from './ru';

/** Та же структура, что у ru.ts: пропущенный ключ — ошибка компиляции. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const en: Shape<typeof ru> = {
  app: {
    name: 'Lumo',
    tagline: 'An infinite whiteboard for teamwork',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    loading: 'Loading…',
    retry: 'Retry',
    copy: 'Copy',
    copied: 'Copied',
    back: 'Back',
    more: 'More',
    search: 'Search',
    done: 'Done',
    soon: 'soon',
    untitled: 'Untitled',
  },
  theme: {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
  time: {
    justNow: 'just now',
  },
  errors: {
    notFound: 'Page not found',
    notFoundHint: 'The link may be outdated or the board was deleted.',
    generic: 'Something went wrong',
    toHome: 'Go home',
  },
  tools: {
    select: 'Select',
    hand: 'Hand',
    sticky: 'Sticky note',
    shape: 'Shape',
    text: 'Text',
    pen: 'Pen',
    highlighter: 'Highlighter',
    eraser: 'Eraser',
    connector: 'Connector',
    frame: 'Frame',
    image: 'Image',
    card: 'Card',
    table: 'Table',
    mindmap: 'Mind map',
    comment: 'Comment',
    stamp: 'Stamp',
    laser: 'Laser pointer',
    undo: 'Undo',
    redo: 'Redo',
    lockHint: 'Double-click to keep the tool',
  },
  shapes: {
    rect: 'Rectangle',
    rounded: 'Rounded',
    ellipse: 'Circle',
    diamond: 'Diamond',
    triangle: 'Triangle',
    star: 'Star',
    hexagon: 'Hexagon',
    arrow: 'Arrow',
    cloud: 'Cloud',
    cylinder: 'Cylinder',
    parallelogram: 'Parallelogram',
  },
  sticky: {
    size: 'Size',
    sizeS: 'S',
    sizeM: 'M',
    sizeL: 'L',
    color: 'Sticky note color',
    showAuthor: 'Show author',
  },
  zoom: {
    in: 'Zoom in',
    out: 'Zoom out',
    fit: 'Zoom to fit',
    reset: 'Zoom to 100%',
    toSelection: 'Zoom to selection',
    minimap: 'Minimap',
  },
  context: {
    color: 'Color',
    fill: 'Fill',
    stroke: 'Stroke',
    strokeWidth: 'Thickness',
    strokeStyle: 'Line style',
    solid: 'Solid',
    dashed: 'Dashed',
    dotted: 'Dotted',
    font: 'Font',
    fontSize: 'Font size',
    autoSize: 'Auto',
    align: 'Alignment',
    alignLeft: 'Align left',
    alignCenter: 'Align center',
    alignRight: 'Align right',
    valignTop: 'Top',
    valignMiddle: 'Middle',
    valignBottom: 'Bottom',
    textColor: 'Text color',
    shapeKind: 'Shape type',
    layers: 'Layers',
    bringToFront: 'Bring to front',
    bringForward: 'Bring forward',
    sendBackward: 'Send backward',
    sendToBack: 'Send to back',
    lock: 'Lock',
    unlock: 'Unlock',
    duplicate: 'Duplicate',
    delete: 'Delete',
    group: 'Group',
    ungroup: 'Ungroup',
    alignObjects: 'Align',
    alignObjLeft: 'Align left edges',
    alignObjCenterX: 'Align horizontal centers',
    alignObjRight: 'Align right edges',
    alignObjTop: 'Align top edges',
    alignObjMiddle: 'Align vertical centers',
    alignObjBottom: 'Align bottom edges',
    distributeH: 'Distribute horizontally',
    distributeV: 'Distribute vertically',
    more: 'More',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strike: 'Strikethrough',
    link: 'Link',
    bulletList: 'Bulleted list',
    background: 'Background',
    none: 'None',
    copy: 'Copy',
    paste: 'Paste',
    cut: 'Cut',
    selected: '{count|object|objects|objects}',
  },
  board: {
    untitled: 'New board',
    rename: 'Rename',
    menu: 'Board menu',
    share: 'Share',
    comments: 'Comments',
    present: 'Present',
    timer: 'Timer',
    voting: 'Voting',
    grid: 'Grid',
    gridDots: 'Dots',
    gridLines: 'Lines',
    gridNone: 'No grid',
    snapToGrid: 'Snap to grid',
    shortcuts: 'Keyboard shortcuts',
    backToDashboard: 'All boards',
    localMode: 'Local board',
    hideUi: 'Hide interface',
    showUi: 'Show interface',
  },
  dashboard: {
    allBoards: 'All boards',
    recent: 'Recent',
    favorites: 'Favorites',
    shared: 'Shared with me',
    templates: 'Templates',
    trash: 'Trash',
    projects: 'Projects',
    newProject: 'New project',
    projectName: 'Project name',
    renameProject: 'Rename project',
    deleteProject: 'Delete project',
    newBoard: 'New board',
    import: 'Import',
    importTooLarge: 'The file is larger than 20 MB',
    importNotJson: 'This is not a JSON export file',
    blankBoard: 'Blank board',
    quickStart: 'Quick start',
    searchBoards: 'Search boards',
    sortUpdated: 'Last modified',
    sortName: 'Name',
    sortCreated: 'Date created',
    viewGrid: 'Grid view',
    viewList: 'List view',
    open: 'Open',
    rename: 'Rename',
    duplicate: 'Duplicate',
    moveTo: 'Move to project',
    noProject: 'No project',
    addFavorite: 'Add to favorites',
    removeFavorite: 'Remove from favorites',
    toTrash: 'Move to trash',
    restore: 'Restore',
    deleteForever: 'Delete forever',
    deleteForeverConfirm: 'The board and all its versions will be deleted permanently.',
    updated: 'Edited {time}',
    deleted: 'Deleted {time}',
    owner: 'Owner',
    name: 'Name',
    emptyAll: 'Nothing here yet',
    emptyAllText: 'Create your first board — sticky notes, diagrams and ideas are waiting.',
    emptyFavorites: 'No favorite boards',
    emptyFavoritesText: 'Star the boards you come back to often.',
    emptyRecent: 'You haven’t opened any boards yet',
    emptyShared: 'Nothing has been shared with you yet',
    emptySharedText: 'Boards you’re invited to will show up here.',
    emptyTrash: 'Trash is empty',
    emptyTrashText: 'Deleted boards stay here for 30 days.',
    emptySearch: 'Nothing found',
    emptySearchText: 'Try a different search.',
    trashHint: 'Boards are deleted permanently after 30 days.',
    workspaces: 'Workspaces',
    newWorkspace: 'New workspace',
    workspaceName: 'Workspace name',
    workspaceSettings: 'Workspace settings',
    profile: 'Profile',
    explore: 'Gallery',
    developers: 'Developers',
    personal: 'Personal',
    members: '{count|member|members|members}',
    boardsCount: '{count|board|boards|boards}',
  },
  share: {
    title: 'Share board',
    invite: 'Invite',
    invitePlaceholder: 'Teammate’s email',
    people: 'People with access',
    generalAccess: 'General access',
    private: 'Invited people only',
    privateHint: 'Only you and the people you invite can see the board',
    workspace: 'Everyone in the workspace',
    workspaceHint: 'Workspace members can edit',
    link: 'Anyone with the link',
    linkHint: 'Access via link without signing in',
    public: 'Public board',
    publicHint: 'Visible to everyone on the internet, can be embedded on a website',
    linkRole: 'Link role',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    password: 'Link password',
    passwordSet: 'Password set',
    passwordRemove: 'Remove password',
    expires: 'Expires',
    never: 'Never',
    day: '1 day',
    week: '7 days',
    month: '30 days',
    expiresAt: 'until {date}',
    rotate: 'Reset link',
    publicUrl: 'Public address',
    listed: 'Show in the gallery',
    listedHint: 'The board will appear on the /explore page',
    allowDuplicate: 'Allow copying to own account',
    tags: 'Tags',
    tagsPlaceholder: 'Comma-separated: retro, product',
    embed: 'Embed on a website',
    embedCopy: 'Copy code',
    pending: 'Invitation sent',
    remove: 'Remove access',
    leave: 'Leave board',
    roles: {
      owner: 'Owner',
      editor: 'Editor',
      commenter: 'Commenter',
      viewer: 'Viewer',
    },
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to continue working on your boards.',
    registerTitle: 'Create an account',
    registerSubtitle: 'Free and unlimited. Every feature is available to everyone.',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    namePlaceholder: 'What should we call you',
    passwordHint: 'At least 8 characters',
    login: 'Sign in',
    register: 'Create account',
    noAccount: 'No account?',
    haveAccount: 'Already have an account?',
    forgot: 'Forgot password?',
    forgotTitle: 'Reset password',
    forgotSubtitle: 'We’ll send you a link to create a new password.',
    forgotSend: 'Send link',
    forgotSent: 'If this email is registered, the message is on its way. Check your inbox.',
    resetTitle: 'New password',
    resetSave: 'Save password',
    newPassword: 'New password',
    backToLogin: 'Back to sign in',
    orContinue: 'or',
    withGithub: 'Sign in with GitHub',
    withGoogle: 'Sign in with Google',
    logout: 'Sign out',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },
  sync: {
    online: 'Online',
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    offline: 'Offline — changes are saved and will be sent later',
    offlineShort: 'Offline',
    synced: 'All changes saved',
    readOnly: 'View only',
    commentOnly: 'Comments only',
    follow: 'Follow',
    stopFollow: 'Stop following',
    following: 'You are following {name}',
    goTo: 'Go to their view',
    summon: 'Bring everyone to me',
    summoned: '{name} is bringing everyone to their view',
    participants: 'Participants',
    you: 'you',
    guest: 'Guest',
    accessDenied: 'No access to this board',
    limit: 'Limit reached',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    tools: 'Tools',
    edit: 'Editing',
    view: 'View',
    navigation: 'Navigation',
    pan: 'Pan',
    panHint: 'Space + drag, middle mouse button',
    zoom: 'Zoom',
    zoomHint: 'Ctrl + wheel, pinch on the trackpad',
    undo: 'Undo',
    redo: 'Redo',
    selectAll: 'Select all',
    duplicate: 'Duplicate',
    group: 'Group',
    ungroup: 'Ungroup',
    lock: 'Lock',
    delete: 'Delete',
    nudge: 'Nudge by 1 / 10 px',
    editText: 'Edit text',
    bringForward: 'Forward / backward',
    bringToFront: 'To front / to back',
    fit: 'Zoom to fit',
    toSelection: 'Zoom to selection',
    zoom100: 'Zoom to 100%',
    zoomInOut: 'Zoom in / out',
    palette: 'Command palette',
    help: 'This cheat sheet',
    hideUi: 'Hide interface',
    multiSelect: 'Add to selection',
    resizeAspect: 'Keep proportions / from center',
    rotateSnap: 'Rotate in 15° steps',
    altDrag: 'Duplicate by dragging',
  },
};
